import React, { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import {
  selectedBranchAtom,
  searchValuesAtom,
  openSnackbarAdviseAtom,
  reloadLeadsAtom,
} from '../../../Atoms';
import {
  LEADS_STANDARD_FILTERS,
  LEADS_SORTING_OPTIONS,
} from '../../../Helpers/Filters/constants';
import mapFilters from '../../../Helpers/Filters/mapFilters';
import saveFiltersInLocal from '../../../Helpers/Filters/saveFiltersInLocal';
import useInfiniteQuery from '../../../Hooks/useInfiniteQuery';
import { download } from '../../../api/utils';
import useAPI from '../../../api';
import SectionPage from '../../../Components/Paper/SectionPage';
import ErrorDialog from '../../../Components/Error';
import LeadsListItem from './Components/LeadsListItem';
import LeadDialog, {
  DEFAULT_PAGES,
} from '../../../Components/Dialog/EntityInfoDialog';
import ConfirmPopup from '../../../Components/Popup/ConfirmPopup';
import LeadDetailsPage from './Components/LeadDetailsPage';

const getLeadsListItem = (data, handleLeadToCustomer) => (
  <LeadsListItem
    key={data.id}
    data={data}
    handleLeadToCustomer={handleLeadToCustomer}
  />
);

const LeadsPage = function LeadsPage() {
  const intl = useIntl();
  const API = useAPI();
  const location = useLocation()?.pathname;
  const sectionName = 'LEADS';

  const searchValues = useAtomValue(searchValuesAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const { id: branchId, branchDescription: branchName } =
    useAtomValue(selectedBranchAtom) || {};
  const [reloadLeads, setReloadLeads] = useAtom(reloadLeadsAtom);

  const [leadDialog, setLeadDialog] = useState(false);
  const [confirmClosing, setConfirmClosing] = useState(null);

  const handleOpenLeadDialog = () => {
    setLeadDialog(true);
  };

  const handleCloseLeadDialog = () => {
    setLeadDialog(false);
  };

  const downloadLeadsFile = useCallback(
    () =>
      download(
        `${sectionName}-${branchName}`,
        API.export2Excel(
          branchId,
          {
            type: 'All',
            sectionName,
          },
          {
            pageNumber: 0,
            filterExpression: mapFilters(
              ['fullNameSearch', 'fiscalCode', 'mobilePhoneNumber'],
              LEADS_STANDARD_FILTERS,
              [{ keyName: 'distanceKm', defaultValue: null }],
              searchValues
            ),
            sortExpression: searchValues?.sortValue
              ? {
                  sortValue: searchValues?.sortValue.value,
                  sort: searchValues?.sortValue.direction,
                }
              : null,
          }
        )
      ),
    [branchId, searchValues]
  );

  const getLeads = useCallback(
    (pageNumber) => {
      return branchId
        ? API.getLeads(
            branchId,
            {
              pageNumber,
              silent: pageNumber > 1,
              filterExpression: mapFilters(
                ['fullNameSearch', 'fiscalCode', 'mobilePhoneNumber'],
                LEADS_STANDARD_FILTERS,
                [{ keyName: 'distanceKm', defaultValue: null }],
                searchValues
              ),
              sortExpression: searchValues?.sortValue
                ? {
                    sortValue: searchValues?.sortValue.value,
                    sort: searchValues?.sortValue.direction,
                  }
                : null,
            },
            () => {
              saveFiltersInLocal(searchValues, location);
              setReloadLeads(false);
            },
            (err) => {
              console.log(err);
              setReloadLeads(false);
            }
          )
        : Promise.resolve([]);
    },
    [branchId, searchValues]
  );

  const {
    items: contacts,
    error,
    ObserverWrapper,
    removeClickedItem,
    recordCount,
    updateItem,
    reload,
  } = useInfiniteQuery(getLeads);

  useEffect(() => {
    if (reloadLeads) {
      reload();
    }
  }, [reloadLeads]);

  const handleSubmit = (payload) => {
    API.addLeads(
      {
        ...payload,
        branchId,
        income: payload?.income !== '' ? payload?.income : null,
        ...(payload?.mobilePhoneNumber
          ? {
              mobilePhoneNumber:
                payload?.mobilePhoneNumber?.substring(0, 3) === '+39'
                  ? payload?.mobilePhoneNumber
                  : `+39${payload?.mobilePhoneNumber}`,
            }
          : {}),
      },
      () => {
        setLeadDialog(false);
        setReloadLeads(true);
        setOpenSnackbarAdvise({
          message: intl.messages.NEW_LEAD_CREATE_SUCCESSFULLY,
          severity: 'success',
        });
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: `${intl.messages.ERROR}: ${
            err?.status ? `${err?.status} - ` : ''
          } ${intl.messages.CREATE_LEAD_ERROR}`,
          severity: 'error',
        });
      }
    );
  };

  const handleLeadToCustomer = (leadId) => {
    API.leadToCustomer(
      leadId,
      () => {
        setReloadLeads(true);
        setOpenSnackbarAdvise({
          message: intl.messages.LEAD_TO_CUSTOMER_SUCCESSFULLY,
          severity: 'success',
        });
      },
      (err) => {
        setOpenSnackbarAdvise({
          message: `${intl.messages.ERROR}: ${
            err?.status ? `${err?.status} - ` : ''
          } ${intl.messages.LEAD_TO_CUSTOMER_ERROR}`,
          severity: 'error',
        });
      }
    );
  };

  return (
    <>
      {error && <ErrorDialog error={error} />}
      <SectionPage
        fab={intl.messages.NEW_LEAD}
        onClickFab={handleOpenLeadDialog}
        fabVariant="extended"
        tablet={false}
        updateItem={updateItem}
        sectionName={sectionName}
        sectionSubName="LEADS"
        heightQuery
        customEntryFunction={(item) =>
          getLeadsListItem(item, handleLeadToCustomer)
        }
        filters={[
          ...[{ keyName: 'distanceKm', defaultValue: null }],
          ...LEADS_STANDARD_FILTERS.map((x) => ({
            ...x,
            defaultValue: x?.keyName === 'leadStatus',
          })),
        ]}
        sortingOptions={LEADS_SORTING_OPTIONS}
        defaultRating={[]}
        contactsCount={contacts.length}
        ExportToExcel={downloadLeadsFile}
        ListObserverWrapper={ObserverWrapper}
        mainBoxWidth="100%"
        removeFromList={(id) => removeClickedItem(id, recordCount - 1)}
      />
      <LeadDialog
        title={intl.messages.NEW_LEAD}
        onSubmit={handleSubmit}
        open={leadDialog}
        branchId={branchId}
        data={leadDialog}
        pages={[LeadDetailsPage, ...DEFAULT_PAGES]}
        close={(e, isFormDirty) => {
          if (isFormDirty) setConfirmClosing(e.currentTarget);
          else {
            setLeadDialog(false); //close directly if no field is touched
          }
        }}
      />
      <ConfirmPopup
        confirmText={intl.messages.CLOSE_WINDOW}
        open={Boolean(confirmClosing)}
        anchorEl={confirmClosing}
        close={() => setConfirmClosing(null)}
        handleConfirm={handleCloseLeadDialog}
      />
    </>
  );
};

export default LeadsPage;
